import React, { useState } from 'react';
import { Button, Icon, Illustration } from '@saviynt/design-system';

import { P, useInvertedLocalize } from '../../../utilities';
import {
  thunks,
  trySavePersonalShortcuts,
  useDispatch,
  useSelector,
} from '../store';

import { invertedMessages } from './messages';

import './Placeholder.css';

interface PlaceholderProps {
  showPromo: boolean;
  version?: string;
}

/**
 * Renders the empty state for the shortcuts widget.
 */
export default function Placeholder({
  showPromo,
  version,
}: PlaceholderProps): React.ReactNode {
  const l = useInvertedLocalize(invertedMessages);
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const isPersonalizationEnabled = useSelector(
    (s) => s.user.domainInfo?.FEATURE_SHORTCUTS_PERSONALIZATION ?? false
  );

  const handleNoThanks = () => {
    (async () => {
      setIsSaving(true);

      const isSuccessful = await trySavePersonalShortcuts(null, version);

      // Don't set isSaving back to false to avoid flicker.
      if (isSuccessful) {
        dispatch(thunks.requestPersonalShortcuts());
      }
    })().catch(() => {
      // How should we handle errors here?
      setIsSaving(false);
    });
  };

  if (!isPersonalizationEnabled) {
    return null;
  }

  if (!showPromo) {
    return (
      <div className='Nav_ShortcutsPlaceholder Nav_ShortcutsPlaceholder--noPromo'>
        <Illustration
          kind='InfoGeneral'
          dataTestId='Nav_ShortcutsWidget_Placeholder'
        />
      </div>
    );
  }

  return (
    <div className='Nav_ShortcutsPlaceholder Nav_ShortcutsPlaceholder--hasPromo'>
      <div className='Nav_ShortcutsPlaceholder-promoLabel'>
        <P>
          {l(
            "Shortcut tiles haven't been selected. Add some to your homepage."
          )}{' '}
          <Icon
            kind='ArrowRight'
            className='Nav_ShortcutsPlaceholder-promoLabel-icon'
          />
        </P>
      </div>
      <Button
        type='button'
        kind='subtle'
        onClick={handleNoThanks}
        isLoading={isSaving}
        loaderText={l('Saving')}>
        {l('No Thanks')}
      </Button>
    </div>
  );
}
